import type { ChannelDoc, ChatMessageDoc } from "@kwp/contracts";

/**
 * Pure chat logic for the screen (no React, no Firestore): unread state, channel ordering and
 * names, merging live and older message pages, optimistic sends and grouping. Tested directly.
 */

export type Channel = ChannelDoc & { id: string };
export type Message = ChatMessageDoc & { id: string };

export interface Member {
  uid: string;
  name: string | null;
  email: string | null;
  role: "owner" | "member";
}

export const PAGE_SIZE = 50;

// ── Unread ──────────────────────────────────────────────────────────────────────────────────

/**
 * A channel is unread when something was posted after my read marker. No marker yet means
 * everything is unread; a channel nobody has posted in is never unread.
 */
export function isUnread(lastMessageAt: Date | null, lastReadAt: Date | null): boolean {
  if (lastMessageAt === null) return false;
  return lastReadAt === null || lastMessageAt.getTime() > lastReadAt.getTime();
}

/** Read markers per channel: `undefined` while the listener has not answered yet. */
export type ReadMarkers = ReadonlyMap<string, Date | null>;

export function unreadIds(channels: readonly Channel[], reads: ReadMarkers, openChannelId: string | null): Set<string> {
  const ids = new Set<string>();
  for (const c of channels) {
    if (c.id === openChannelId || !reads.has(c.id)) continue;
    if (isUnread(c.lastMessageAt, reads.get(c.id) ?? null)) ids.add(c.id);
  }
  return ids;
}

// ── Channel list ────────────────────────────────────────────────────────────────────────────

export function displayName(member: Member | undefined, fallback: string): string {
  if (member === undefined) return fallback;
  if (member.name !== null && member.name.trim().length > 0) return member.name;
  if (member.email !== null) return member.email;
  return fallback;
}

/** A DM is named after the other people in it (or "you" for a note-to-self DM). */
export function channelLabel(channel: Channel, myUid: string, members: ReadonlyMap<string, Member>): string {
  if (channel.kind === "channel") return `#${channel.name}`;
  const others = channel.memberUids.filter((u) => u !== myUid);
  if (others.length === 0) return "You";
  return others.map((u) => displayName(members.get(u), channel.name)).join(", ");
}

/** Channels alphabetically, then DMs with the most recent first. */
export function sortChannels(channels: readonly Channel[]): { channels: Channel[]; dms: Channel[] } {
  const time = (c: Channel) => (c.lastMessageAt === null ? 0 : c.lastMessageAt.getTime());
  return {
    channels: channels.filter((c) => c.kind === "channel").sort((a, b) => a.name.localeCompare(b.name)),
    dms: channels.filter((c) => c.kind === "dm").sort((a, b) => time(b) - time(a) || a.id.localeCompare(b.id)),
  };
}

/** The two listeners (public channels, my DMs) answer separately; this merges them by id. */
export function mergeChannelLists(...lists: ReadonlyArray<readonly Channel[]>): Channel[] {
  const byId = new Map<string, Channel>();
  for (const list of lists) for (const c of list) byId.set(c.id, c);
  return [...byId.values()];
}

// ── Message pages ───────────────────────────────────────────────────────────────────────────

/**
 * Everything loaded for one channel. The live listener covers the latest page; older pages are
 * fetched once each. Messages are never deleted, so one that falls out of the live window (new
 * posts push it out) is kept, not dropped: that is what keeps the history gap-free.
 */
export interface MessageState {
  byId: ReadonlyMap<string, Message>;
  /** False once a page came back shorter than PAGE_SIZE: the channel's start is loaded. */
  hasOlder: boolean;
}

export const emptyMessages: MessageState = { byId: new Map(), hasOlder: true };

function compare(a: Message, b: Message): number {
  return a.createdAt.getTime() - b.createdAt.getTime() || a.id.localeCompare(b.id);
}

/** Folds a snapshot of the live (latest) page into the state; edits replace older copies. */
export function mergeLive(state: MessageState, page: readonly Message[], initial: boolean): MessageState {
  const byId = new Map(state.byId);
  for (const m of page) byId.set(m.id, m);
  return { byId, hasOlder: initial ? page.length >= PAGE_SIZE : state.hasOlder };
}

/** Folds an older page (fetched with a cursor) into the state. */
export function mergeOlder(state: MessageState, page: readonly Message[]): MessageState {
  const byId = new Map(state.byId);
  // A message already present may be newer (an edit seen live), so it wins.
  for (const m of page) if (!byId.has(m.id)) byId.set(m.id, m);
  return { byId, hasOlder: page.length >= PAGE_SIZE };
}

export function ordered(state: MessageState): Message[] {
  return [...state.byId.values()].sort(compare);
}

/** The cursor for the next older page: the oldest message loaded. */
export function oldest(state: MessageState): Message | null {
  let found: Message | null = null;
  for (const m of state.byId.values()) if (found === null || compare(m, found) < 0) found = m;
  return found;
}

// ── Optimistic sends ────────────────────────────────────────────────────────────────────────

export interface Pending {
  key: string;
  body: string;
  /** Images to upload before the post (WP-058); kept so Try again can send them again. */
  files: readonly File[];
  createdAt: Date;
  status: "sending" | "failed";
  /** The id the api returned; the pending row disappears once that message is in the list. */
  id: string | null;
}

/** Pending rows that the listener has not delivered yet. */
export function visiblePending(pending: readonly Pending[], state: MessageState): Pending[] {
  return pending.filter((p) => p.id === null || !state.byId.has(p.id));
}

// ── Grouping ────────────────────────────────────────────────────────────────────────────────

const GROUP_GAP_MS = 5 * 60_000;

export interface MessageGroup {
  key: string;
  authorUid: string;
  kind: "user" | "system";
  startedAt: Date;
  messages: Message[];
}

/**
 * Consecutive messages by the same author within five minutes share one header. A system
 * message always stands alone.
 */
export function groupMessages(messages: readonly Message[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  for (const m of messages) {
    const last = groups.at(-1);
    const lastMessage = last?.messages.at(-1);
    const joins =
      last !== undefined &&
      lastMessage !== undefined &&
      m.kind === "user" &&
      last.kind === "user" &&
      last.authorUid === m.authorUid &&
      m.createdAt.getTime() - lastMessage.createdAt.getTime() <= GROUP_GAP_MS;
    if (joins) last.messages.push(m);
    else groups.push({ key: m.id, authorUid: m.authorUid, kind: m.kind, startedAt: m.createdAt, messages: [m] });
  }
  return groups;
}
